/* eslint-disable camelcase */
import Intercom from '@intercom/messenger-js-sdk';
import { UserProfile } from 'components/account/AccountView/users/types';
import { User } from 'types';
import { api } from '../controllers/network/apiClient';
import { parseJwt } from './auth';
import { INTERCOM_APP_ID } from './envVars';

const INTERCOM_JWT_ENDPOINT = '/v2/shops/intercom-jwt/';

const safeParseJwt = (jwt: string) => {
  try {
    return parseJwt(jwt) as { shop_id?: number; shop_plan_name?: string };
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

const buildIntercomConfig = (
  user: Partial<User>,
  jwt?: string,
  profile?: UserProfile | null,
) => {
  if (!user?.id) return null;

  if (!INTERCOM_APP_ID) {
    console.error('Intercom app ID is not configured.');
    return null;
  }

  const jwtPayload = jwt ? safeParseJwt(jwt) : null;
  const shopId = jwtPayload?.shop_id || user.shop_id;

  if (!shopId) {
    console.warn('Intercom: No shop_id available.');
    return null;
  }

  const createdAt = user.installed_at
    ? Math.floor(new Date(user.installed_at).getTime() / 1000)
    : undefined;

  const profileName = [profile?.firstName, profile?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();

  const merchantName = profileName || user.shop_name;

  return {
    app_id: INTERCOM_APP_ID,
    user_id: `${user.id}_${shopId}`,
    name: merchantName || user.username,
    email: user.postscript_contact_email || '',
    company: { company_id: shopId.toString() },
    ...(createdAt && { created_at: createdAt }),
    ...(jwt && { intercom_user_jwt: jwt }),
    ...(shopId && { shop_id: shopId }),
    ...(user.domain && { shopify_domain_url: user.domain }),
    ...(jwtPayload?.shop_plan_name && {
      shop_plan_name: jwtPayload.shop_plan_name,
    }),
  };
};

interface IntercomJWTResponse {
  intercom_jwt: string;
}

export const initializeIntercom = async (
  user: Partial<User>,
  profile?: UserProfile | null,
): Promise<boolean> => {
  try {
    let jwt: string | undefined;
    try {
      const response = await api.get<IntercomJWTResponse>(
        INTERCOM_JWT_ENDPOINT,
      );
      if (
        response &&
        typeof response === 'object' &&
        'intercom_jwt' in response
      ) {
        jwt = response.intercom_jwt;
      } else {
        console.error(
          'Intercom JWT endpoint returned invalid response. Messenger Security requires a valid JWT.',
        );
        return false;
      }
    } catch (error) {
      console.error(
        'Failed to fetch Intercom JWT. Messenger Security requires a valid JWT:',
        error,
      );
      return false;
    }

    const config = buildIntercomConfig(user, jwt, profile);
    if (config) {
      Intercom(config);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error initializing Intercom:', error);
    return false;
  }
};

export const hideIntercomWidget = (): void => {
  try {
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('hide');
    }
  } catch (error) {
    console.error('Error hiding Intercom widget:', error);
  }
};

export const showIntercomWidget = (): void => {
  try {
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('show');
    }
  } catch (error) {
    console.error('Error showing Intercom widget:', error);
  }
};

export const shutdownIntercom = (): void => {
  try {
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('shutdown');
    }
  } catch (error) {
    console.error('Error shutting down Intercom:', error);
  }
};
